import { get, type StudentDetail } from "../api.js";
import { formatDate, formatDuration } from "../format.js";
import { PracticeAttachments } from "../PracticeAttachments.js";
import { useAsync } from "../useAsync.js";

const RECENT_WORK_LIMIT = 5;

type RecentPracticeSession = StudentDetail["recentPractice"][number];

type StudentPracticePage = {
  sessions: RecentPracticeSession[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPracticeAttachment(
  value: unknown,
): value is RecentPracticeSession["attachments"][number] {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    (value.kind === "file" || value.kind === "youtube") &&
    typeof value.fileName === "string" &&
    (typeof value.fileType === "string" || value.fileType === null) &&
    (typeof value.videoId === "string" || value.videoId === null)
  );
}

function isRecentPracticeSession(value: unknown): value is RecentPracticeSession {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.startedAt === "string" &&
    typeof value.durationSeconds === "number" &&
    Number.isFinite(value.durationSeconds) &&
    (typeof value.name === "string" || value.name === null) &&
    typeof value.needsWork === "boolean" &&
    typeof value.autoStopped === "boolean" &&
    (value.loggedBy === "student" || value.loggedBy === "family" || value.loggedBy === null) &&
    Array.isArray(value.attachments) &&
    value.attachments.every(isPracticeAttachment)
  );
}

async function loadStudentPracticePage(studentId: string): Promise<StudentPracticePage> {
  const response = await get<unknown>(
    `/admin/students/${encodeURIComponent(studentId)}/practice/sessions`,
    { page: "1", limit: String(RECENT_WORK_LIMIT) },
  );
  if (
    !isRecord(response) ||
    !Array.isArray(response.sessions) ||
    !response.sessions.every(isRecentPracticeSession)
  ) {
    throw new Error("Recent student work returned an unexpected response.");
  }
  return { sessions: response.sessions };
}

/**
 * Read the same staff-scoped practice history used by the student record.
 * Keep it separate from the lesson and note reads so a missing practice
 * service never prevents a tutor from recording the lesson.
 */
export function LessonStudentWorkPanel({ studentId }: { studentId: string }) {
  const { data, error, loading, reload } = useAsync(
    () => loadStudentPracticePage(studentId),
    [studentId],
  );

  return (
    <section className="panel" aria-labelledby="lesson-student-work-heading">
      <h2 id="lesson-student-work-heading">Recent student work</h2>
      <p className="small muted">
        Practice activity and work shared between lessons.
      </p>

      {loading && !data && <p className="empty" role="status">Loading recent student work…</p>}
      {loading && data && <p className="small muted" role="status">Refreshing recent student work…</p>}
      {error && (
        <div className="small" role="alert">
          <p>
            {data
              ? "Recent work could not be refreshed. The last loaded entries are shown."
              : "Recent student work could not be loaded. The lesson note is still available."}
          </p>
          <button type="button" className="quiet small" onClick={reload}>
            Retry student work
          </button>
        </div>
      )}

      {data && data.sessions.length === 0 && !error && (
        <p className="small muted">No recent practice work has been shared.</p>
      )}
      {data && data.sessions.length > 0 && (
        <div className="lesson-student-work-list">
          {data.sessions.map((session) => (
            <article className="panel lesson-student-work-session" key={session.id}>
              <div className="lesson-student-work-header">
                <div>
                  <strong>{session.name ?? "Practice session"}</strong>
                  <div className="small muted">
                    {formatDate(session.startedAt)} · {formatDuration(session.durationSeconds)}
                    {session.loggedBy === "student" ? " · logged by student" : ""}
                    {session.loggedBy === "family" ? " · logged at home" : ""}
                  </div>
                </div>
                <div className="lesson-student-work-tags">
                  {session.needsWork && <span className="tag warn">Asked for help</span>}
                  {session.autoStopped && <span className="tag">Timer stopped</span>}
                </div>
              </div>
              {session.attachments.length > 0 && (
                <div className="lesson-student-work-attachments">
                  <h3 className="small">Shared work</h3>
                  <PracticeAttachments
                    base={`/admin/students/${encodeURIComponent(studentId)}/practice/${encodeURIComponent(session.id)}/attachments`}
                    attachments={session.attachments}
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
